import React, { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { apiClient, UserContextResponse } from '../../lib/api';

/**
 * The org chain above an account, with the catalogs attached at each level.
 *
 * "Why is this clinic seeing that price" is otherwise a multi-screen hunt: the
 * answer is which catalog is assigned where, and the assignment lives at a
 * different level for every account. Showing supervisor → AM → them in one
 * column, each with its catalogs, answers it in a glance.
 *
 * Self-loading so it can be dropped into any modal without the host having to
 * know about the directory API.
 */
/**
 * The account's derived state and blockers, for the top of the modal.
 *
 * The directory is organised around "what is this account waiting on", and
 * opening a row used to lose that entirely — the detail view never mentioned
 * it. Both this and the chain below come from the same context fetch.
 */
export function UserStateBanner({ userId }: { userId: string }) {
  const [ctx, setCtx] = useState<UserContextResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient.getUserContext(userId).then(res => {
      if (!cancelled && res.success && res.data) setCtx(res.data);
    });
    return () => { cancelled = true; };
  }, [userId]);

  if (!ctx) return null;
  const { state, blockers } = ctx.user;

  const tone: Record<string, string> = {
    needs_approval: 'bg-amber-50 border-amber-200 text-amber-900',
    awaiting_network: 'bg-orange-50 border-orange-200 text-orange-900',
    awaiting_id_review: 'bg-blue-50 border-blue-200 text-blue-900',
    onboarding: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    never_logged_in: 'bg-gray-50 border-gray-200 text-gray-700',
    active: 'bg-green-50 border-green-200 text-green-900',
    suspended: 'bg-red-50 border-red-200 text-red-900'
  };
  const label: Record<string, string> = {
    needs_approval: 'Needs approval', awaiting_network: 'Awaiting network',
    awaiting_id_review: 'Awaiting ID review', onboarding: 'Onboarding',
    never_logged_in: 'Never logged in', active: 'Active', suspended: 'Suspended'
  };
  const age = (since?: string) => {
    if (!since) return null;
    const d = Math.floor((Date.now() - new Date(since).getTime()) / 86400000);
    return d < 0 ? null : d === 0 ? 'today' : d < 30 ? `${d} days` : `${Math.floor(d / 30)} months`;
  };

  return (
    <div className={`border rounded-lg px-4 py-3 mb-4 ${tone[state] || tone.active}`}>
      <div className="font-semibold text-sm">{label[state] || state}</div>
      {blockers.length === 0
        ? <div className="text-sm opacity-80 mt-0.5">Nothing is blocking this account.</div>
        : (
          <ul className="mt-1 space-y-0.5">
            {blockers.map(b => {
              const waited = age(b.since);
              return (
                <li key={b.code} className="text-sm opacity-90">
                  {b.action}
                  <span className="opacity-70">
                    {' '}({b.owner === 'admin' ? 'us' : 'them'}
                    {waited ? `, waiting ${waited}` : ''})
                  </span>
                </li>
              );
            })}
          </ul>
        )}
    </div>
  );
}

export function UserChainPanel({ userId, onOpenUser, amActions, footer, below }: {
  userId: string;
  /** Jump to another account in the chain without leaving the modal. */
  onOpenUser?: (id: string) => void;
  /** Reassign control, rendered on the AM node it acts on. */
  amActions?: React.ReactNode;
  /** Rendered under the tree — the product list belongs with the catalogs. */
  footer?: React.ReactNode;
  /**
   * Accounts BELOW this one — an AM's clinics, a supervisor's AMs and clinics.
   * They belong in the same hierarchy block as everything above: one place that
   * answers "where does this account sit", rather than a tree at the bottom and
   * separate collapsibles further up.
   */
  below?: React.ReactNode;
}) {
  const [ctx, setCtx] = useState<UserContextResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    apiClient.getUserContext(userId).then(res => {
      if (cancelled) return;
      if (res.success && res.data) setCtx(res.data);
      else setFailed(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [userId]);

  // A failure here must never take the rest of the modal down with it — the
  // panel is supplementary to everything above it.
  if (failed) return null;

  const levelLabel = (level: string) =>
    level === 'self' ? 'This account' : level === 'am' ? 'Account manager' : 'Supervisor';

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-3">
        Hierarchy &amp; catalogs above this account
      </h4>

      {loading && <p className="text-sm text-gray-500">Loading hierarchy…</p>}

      {!loading && ctx && (
        <>
          <ol className="space-y-2">
            {ctx.chain.map((link, i) => (
              <li
                key={link.id}
                className={`border border-gray-200 rounded-md p-3 ${link.level === 'self' ? 'bg-gray-50' : ''}`}
                style={{ marginLeft: i * 16 }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-xs uppercase tracking-wide text-gray-500">
                      {levelLabel(link.level)}
                    </span>
                    <div className="font-medium text-gray-900">{link.name}</div>
                  </div>
                  {/* Deep link. The AM and supervisor were names on a card with
                      no way through to them — you had to close, search, reopen. */}
                  {link.level !== 'self' && onOpenUser && (
                    <button
                      onClick={() => onOpenUser(link.id)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 text-gray-700 flex-shrink-0"
                      title={`Open ${link.name}`}
                    >
                      Open <ArrowUpRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {link.level === 'am' && amActions && (
                  <div className="mt-2">{amActions}</div>
                )}
                <div className="mt-2">
                  <span className="text-xs text-gray-500">Catalogs: </span>
                  {link.catalogs.length === 0
                    ? <span className="text-xs text-gray-400">none assigned</span>
                    : link.catalogs.map(c => (
                      <span
                        key={c.id}
                        className="inline-block px-2 py-0.5 mr-1 mb-1 rounded bg-blue-50 text-blue-700 text-xs"
                      >
                        {c.name}
                      </span>
                    ))}
                </div>
              </li>
            ))}
          </ol>

          {below && <div className="mt-3 space-y-3">{below}</div>}

          {footer && <div className="mt-3">{footer}</div>}

          {ctx.chain.length === 1 && (
            <p className="text-xs text-gray-500 mt-2">
              No AM or supervisor above this account — it sits with the House.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default UserChainPanel;
